import React, { createContext, useContext, useState } from 'react';

const translations = {
  es: {
    // Navbar
    nav: {
      properties: 'Propiedades',
      calculator: 'Cash Flow',
      zones: 'Zonas',
      contact: 'Contacto',
      freeConsult: 'Asesoría gratuita'
    },
    // Hero
    hero: {
      title1: 'Invierte en Riviera Maya.',
      title2: 'Propiedades que se pagan solas.',
      subtitle: 'Cash flow desde el primer mes · Plusvalía 8–15% anual · Certeza jurídica · Lujo sostenible',
      cta: 'Quiero una asesoría sin costo'
    },
    // Value Proposition
    value: {
      sectionTitle: 'Por qué invertir con nosotros',
      roi: {
        title: 'Rendimiento real',
        text: 'Analizamos cada propiedad con datos reales de ocupación, tarifas Airbnb y gastos operativos. Sin estimados inflados.'
      },
      legal: {
        title: 'Certeza jurídica',
        text: 'Solo trabajamos con proyectos con MIA vigente, fideicomiso en regla y desarrolladores con historial comprobado.'
      },
      sustainable: {
        title: 'Nuevo lujo sostenible',
        text: 'Proyectos con paneles solares, planta de agua y baja densidad. Propiedades que mantienen y aumentan su valor.'
      }
    },
    // Featured Properties
    properties: {
      sectionTitle: 'Propiedades destacadas',
      sectionSubtitle: 'Curadas por rentabilidad, certeza jurídica y plusvalía',
      zone: 'Zona',
      type: 'Tipo',
      priceFrom: 'Precio desde',
      estimatedROI: 'Rendimiento estimado',
      projectedOccupancy: 'Ocupación proyectada',
      viewDetails: 'Ver detalles',
      property1: {
        zone: 'Tulum · Región 15',
        type: 'Departamento 2 recámaras'
      },
      property2: {
        zone: 'Playa del Carmen · Colosio',
        type: 'Departamento 3 recámaras'
      },
      property3: {
        zone: 'Puerto Morelos',
        type: 'Departamento 2 recámaras'
      }
    },
    // Cash Flow Calculator
    calculator: {
      sectionTitle: '¿Cuánto ingreso mensual genera tu propiedad?',
      sectionSubtitle: 'Simula tu cash flow neto en segundos',
      investmentAmount: 'Monto de inversión',
      estimatedOccupancy: 'Ocupación estimada',
      nightlyRate: 'Tarifa por noche',
      annualIncome: 'Ingreso anual estimado',
      annualROI: 'Rendimiento anual',
      recoveryYears: 'Años de recuperación',
      cta: 'Quiero propiedades con este perfil'
    },
    // Zones
    zones: {
      sectionTitle: '¿Dónde invertir en 2026?',
      tulum: {
        name: 'TULUM',
        description: 'Epicentro del diseño arquitectónico y la sostenibilidad. Región 15 y La Veleta: alta demanda, alta plusvalía.',
        ticket: 'Ticket promedio: $180,000 – $400,000 USD'
      },
      playaDelCarmen: {
        name: 'PLAYA DEL CARMEN',
        description: 'Zonas Colosio y Ejidal en plena gentrificación. Alta demanda de nómadas digitales. Conectividad total.',
        ticket: 'Ticket promedio: $120,000 – $280,000 USD'
      },
      puertoMorelos: {
        name: 'PUERTO MORELOS',
        description: 'El líder silencioso de 2026. Mayor tasa de absorción, precios de entrada accesibles, ambiente residencial.',
        ticket: 'Ticket promedio: $90,000 – $200,000 USD'
      }
    },
    // Testimonials
    testimonials: {
      sectionTitle: 'Lo que dicen nuestros inversionistas',
      testimonial1: {
        quote: 'Marea Riviera nos ayudó a encontrar una propiedad en Tulum que genera $1,800 USD mensuales de renta. El proceso fue claro y sin sorpresas.',
        author: 'Carlos M., inversionista desde CDMX'
      },
      testimonial2: {
        quote: 'Lo que más me convenció fue el análisis de rendimiento detallado. No me vendieron un sueño, me presentaron números reales y se cumplieron.',
        author: 'Sarah K., inversionista desde Toronto'
      }
    },
    // Contact Form
    contact: {
      sectionTitle: 'Agenda tu consulta sin costo',
      sectionSubtitle: 'Te respondemos en menos de 2 horas en días hábiles.',
      fullName: 'Nombre completo',
      email: 'Correo electrónico',
      phone: 'Teléfono / WhatsApp',
      country: 'País de residencia',
      investmentAmount: 'Monto aproximado de inversión',
      investmentGoal: '¿Qué buscas principalmente?',
      message: 'Mensaje (opcional)',
      submit: 'Quiero mi asesoría gratuita',
      successMessage: '¡Gracias! Te contactamos en menos de 2 horas.',
      investmentOptions: {
        lessThan100k: 'Menos de $100,000 USD',
        between100kAnd200k: '$100,000 – $200,000 USD',
        between200kAnd500k: '$200,000 – $500,000 USD',
        moreThan500k: 'Más de $500,000 USD'
      },
      goalOptions: {
        cashFlow: 'Cash flow (renta vacacional)',
        appreciation: 'Plusvalía a largo plazo',
        both: 'Ambas'
      },
      required: 'Este campo es requerido',
      invalidEmail: 'Correo electrónico inválido'
    },
    // Footer
    footer: {
      tagline: 'Real Estate Investment',
      rights: '© 2026 Marea Riviera · Todos los derechos reservados',
      legal: 'Inversión inmobiliaria en Riviera Maya, México'
    }
  },
  en: {
    // Navbar
    nav: {
      properties: 'Properties',
      calculator: 'Cash Flow',
      zones: 'Zones',
      contact: 'Contact',
      freeConsult: 'Free consultation'
    },
    // Hero
    hero: {
      title1: 'Invest in Riviera Maya.',
      title2: 'Properties that pay for themselves.',
      subtitle: 'Cash flow from month one · 8–15% annual appreciation · Legal certainty · Sustainable luxury',
      cta: 'I want a free consultation'
    },
    // Value Proposition
    value: {
      sectionTitle: 'Why invest with us',
      roi: {
        title: 'Real returns',
        text: 'We analyze every property with real occupancy data, Airbnb rates, and operating costs. No inflated estimates.'
      },
      legal: {
        title: 'Legal certainty',
        text: 'We only work with projects with current MIA, proper trust, and developers with proven track records.'
      },
      sustainable: {
        title: 'New sustainable luxury',
        text: 'Projects with solar panels, water treatment, and low density. Properties that maintain and increase their value.'
      }
    },
    // Featured Properties
    properties: {
      sectionTitle: 'Featured Properties',
      sectionSubtitle: 'Curated for profitability, legal certainty, and appreciation',
      zone: 'Zone',
      type: 'Type',
      priceFrom: 'Price from',
      estimatedROI: 'Estimated yield',
      projectedOccupancy: 'Projected occupancy',
      viewDetails: 'View details',
      property1: {
        zone: 'Tulum · Region 15',
        type: '2-bedroom apartment'
      },
      property2: {
        zone: 'Playa del Carmen · Colosio',
        type: '3-bedroom apartment'
      },
      property3: {
        zone: 'Puerto Morelos',
        type: '2-bedroom apartment'
      }
    },
    // Cash Flow Calculator
    calculator: {
      sectionTitle: 'How much monthly income does your property generate?',
      sectionSubtitle: 'Simulate your net cash flow in seconds',
      investmentAmount: 'Investment amount',
      estimatedOccupancy: 'Estimated occupancy',
      nightlyRate: 'Nightly rate',
      annualIncome: 'Estimated annual income',
      annualROI: 'Annual yield',
      recoveryYears: 'Recovery years',
      cta: 'I want properties with this profile'
    },
    // Zones
    zones: {
      sectionTitle: 'Where to invest in 2026?',
      tulum: {
        name: 'TULUM',
        description: 'Epicenter of architectural design and sustainability. Region 15 and La Veleta: high demand, high appreciation.',
        ticket: 'Average ticket: $180,000 – $400,000 USD'
      },
      playaDelCarmen: {
        name: 'PLAYA DEL CARMEN',
        description: 'Colosio and Ejidal areas in full gentrification. High demand from digital nomads. Total connectivity.',
        ticket: 'Average ticket: $120,000 – $280,000 USD'
      },
      puertoMorelos: {
        name: 'PUERTO MORELOS',
        description: 'The silent leader of 2026. Highest absorption rate, accessible entry prices, residential atmosphere.',
        ticket: 'Average ticket: $90,000 – $200,000 USD'
      }
    },
    // Testimonials
    testimonials: {
      sectionTitle: 'What our investors say',
      testimonial1: {
        quote: 'Marea Riviera helped us find a property in Tulum that generates $1,800 USD monthly in rent. The process was clear and without surprises.',
        author: 'Carlos M., investor from Mexico City'
      },
      testimonial2: {
        quote: 'What convinced me the most was the detailed profitability analysis. They didn\'t sell me a dream, they presented real numbers and they were met.',
        author: 'Sarah K., investor from Toronto'
      }
    },
    // Contact Form
    contact: {
      sectionTitle: 'Schedule your free consultation',
      sectionSubtitle: 'We respond in less than 2 hours on business days.',
      fullName: 'Full name',
      email: 'Email',
      phone: 'Phone / WhatsApp',
      country: 'Country of residence',
      investmentAmount: 'Approximate investment amount',
      investmentGoal: 'What are you mainly looking for?',
      message: 'Message (optional)',
      submit: 'I want my free consultation',
      successMessage: 'Thank you! We\'ll contact you in less than 2 hours.',
      investmentOptions: {
        lessThan100k: 'Less than $100,000 USD',
        between100kAnd200k: '$100,000 – $200,000 USD',
        between200kAnd500k: '$200,000 – $500,000 USD',
        moreThan500k: 'More than $500,000 USD'
      },
      goalOptions: {
        cashFlow: 'Cash flow (vacation rental)',
        appreciation: 'Long-term appreciation',
        both: 'Both'
      },
      required: 'This field is required',
      invalidEmail: 'Invalid email'
    },
    // Footer
    footer: {
      tagline: 'Real Estate Investment',
      rights: '© 2026 Marea Riviera · All rights reserved',
      legal: 'Real estate investment in Riviera Maya, Mexico'
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'es' ? 'en' : 'es');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
